import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, TextField, Button,
  IconButton, Tooltip, CircularProgress, Alert, Autocomplete,
  Dialog, DialogContent, DialogActions, Tabs, Tab, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assignment as RequestIcon, Add as AddIcon, Close as CloseIcon,
  CheckCircle as ApproveIcon, Cancel as RejectIcon,
  Payments as PayIcon, Refresh as RefreshIcon,
  HourglassBottom as WaitingIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ─────────────────────────────────────────────────────────────────
// EXPENDITURE REQUESTS — ask before you spend.
//
// The screen half of the flow the cashbook now enforces. When
// expenditure approval is switched on, money cannot leave without a
// request that has been signed off, so this is where the asking and the
// signing happen.
//
// THE FORM DELIBERATELY HAS NO CHEQUE NUMBER
//   Nobody knows the cheque number when they are asking for money. It
//   is filled in later, on the Payment Voucher, by whoever actually
//   writes the cheque — which is the whole reason this stage exists
//   separately from the voucher.
//
// THREE LISTS, NOT ONE
//   "Mine" is what I asked for and where it has got to.
//   "To approve" is only what I can sign, and never my own.
//   "Ready to pay" is signed off and waiting for a cheque.
//   One combined list would make every user scan for the rows that
//   concern them; these are three different jobs done by three
//   different people.
// ─────────────────────────────────────────────────────────────────

const money = (v) => Number(v || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const STATUS_TONE = {
  SUBMITTED:   'warning',
  ENDORSED:    'info',
  APPROVED_L1: 'info',
  APPROVED:    'success',
  PAID:        'default',
  REJECTED:    'error',
};

const STATUS_LABEL = {
  SUBMITTED:   'Awaiting endorsement',
  ENDORSED:    'Endorsed',
  APPROVED_L1: '1st level approved',
  APPROVED:    'Approved — ready to pay',
  PAID:        'Paid',
  REJECTED:    'Refused',
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10)
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Raise a request ───────────────────────────────────────────────
export function ExpenditureRequestDialog({ open, onClose, categories, expTypes, onSaved }) {
  const blankItem = () => ({
    exp_cat: '', exp_ty: '', request_holder: '',
    narration: '', quantity: '1', unit_price: '', amount: '',
  });
  const [form, setForm]   = useState({});
  const [items, setItems] = useState([blankItem()]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      requisitioner: '', department: '', supplier: '', supplier_contact: '',
      date_needed: '', reason: '', payee_name: '',
    });
    setItems([blankItem()]);
    setError('');
  }, [open]);

  const patch = (i, p) => setItems(prev => prev.map((it, x) => x === i ? { ...it, ...p } : it));
  const addItem = () => setItems(prev => [...prev, blankItem()]);
  const dropItem = (i) => setItems(prev => prev.length > 1 ? prev.filter((_, x) => x !== i) : prev);

  // Quantity x unit price, unless a flat amount was typed. The paper
  // form has both columns and people use whichever suits what they are
  // asking for — "3 reams at 60" or just "printing, 200".
  const lineTotal = (it) => {
    const flat = Number(it.amount);
    if (flat > 0) return flat;
    return (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
  };
  const total = useMemo(
    () => items.reduce((s, it) => s + lineTotal(it), 0), [items]);

  // `name`, NOT `ty`. GET /expenditures selects `e.name` from exp_reg —
  // there is no `ty` column on the row — so `t.ty` was undefined for
  // every type, and the options array was a list of undefineds.
  //
  // That is what blanked the screen. The dialog itself rendered because
  // an Autocomplete with junk options still draws its input; it was
  // OPENING the list that crashed, because MUI's default
  // getOptionLabel reads `option.label` and undefined has no properties.
  // React unmounts the whole tree on a render throw, so a mistyped
  // field name showed up as a white page rather than a missing dropdown.
  //
  // Blanks and duplicates are dropped for the same reason: one null in
  // the list takes the page down with it.
  const typesFor = (cat) => [...new Set(
    (expTypes || [])
      .filter(t => !cat || t.cat === cat)
      .map(t => t?.name)
      .filter(Boolean)
  )];

  const categoryNames = useMemo(() => [...new Set(
    (categories || []).map(c => c?.name).filter(Boolean)
  )], [categories]);

  // ── What is still missing ───────────────────────────────────────
  //
  // The server requires FOUR things per item — category, type, what it
  // is for, and a request holder — but the button only checked the
  // requisitioner and the total. So a row with an amount and a blank
  // category sailed past the button and came back as a server error.
  //
  // Worse, `create()` DROPS incomplete items before it counts them
  // (`.filter(it => it.exp_cat && it.exp_ty && it.amount > 0)`), so the
  // one visible row with a figure in it could be silently discarded and
  // the refusal read "At least one item is required" — while the screen
  // plainly showed an item. Naming the missing field here means that
  // round trip never happens.
  const problem = useMemo(() => {
    if (!form.requisitioner?.trim()) return "Enter the requisitioner's name";
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const n = items.length > 1 ? `Item ${i + 1}: ` : '';
      if (!it.exp_cat)               return `${n}choose a category`;
      if (!it.exp_ty)                return `${n}choose a type`;
      if (!String(it.narration || '').trim())      return `${n}say what it is for`;
      if (!String(it.request_holder || '').trim()) return `${n}enter the request holder`;
      if (!(lineTotal(it) > 0))      return `${n}enter a unit price or a total`;
    }
    if (!(total > 0)) return 'The total must be more than zero';
    return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.requisitioner, items, total]);

  const submit = async () => {
    if (problem) { setError(problem); return; }
    setSaving(true); setError('');
    try {
      const r = await API.post('/expenditure-requests', {
        ...form,
        items: items.map(it => ({
          exp_cat: it.exp_cat, exp_ty: it.exp_ty,
          request_holder: it.request_holder, narration: it.narration,
          quantity: it.quantity, unit_price: it.unit_price,
          amount: it.amount,
        })),
      });
      swal.success(`${r.data.ref_no} submitted for approval`);
      onSaved && (await onSaved(r.data));
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not submit the request');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 60%, ${t.palette.info.main} 100%)`,
        color: '#fff', px: 3, py: 2.2,
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44 }}>
            <RequestIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}>
              Cashbook · Expenditure Approval
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              New Expenditure Request
            </Typography>
          </Box>
          <Chip label={`GHS ${money(total)}`} sx={{
            bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 800 }} />
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f6f7fb' }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2, py: 0.3 }}>
          This is a request to spend, not a payment. There is no cheque number here —
          that is filled in later by whoever writes the cheque, once this has been
          signed off.
        </Alert>

        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="overline" fontWeight={800} color="text.secondary">
              Who is asking
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
              <TextField size="small" fullWidth required label="Requisitioner's name"
                value={form.requisitioner || ''}
                onChange={e => setForm(f => ({ ...f, requisitioner: e.target.value }))} />
              <TextField size="small" fullWidth label="Department"
                value={form.department || ''}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))} />
              <TextField size="small" fullWidth label="Date needed" type="date"
                InputLabelProps={{ shrink: true }}
                value={form.date_needed || ''}
                onChange={e => setForm(f => ({ ...f, date_needed: e.target.value }))} />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
              <TextField size="small" fullWidth label="Suggested supplier"
                value={form.supplier || ''}
                onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} />
              <TextField size="small" fullWidth label="Supplier address / tel"
                value={form.supplier_contact || ''}
                onChange={e => setForm(f => ({ ...f, supplier_contact: e.target.value }))} />
              <TextField size="small" fullWidth label="Payee name for the cheque"
                placeholder="Defaults to the requisitioner"
                value={form.payee_name || ''}
                onChange={e => setForm(f => ({ ...f, payee_name: e.target.value }))} />
            </Stack>
            <TextField size="small" fullWidth multiline minRows={2} sx={{ mt: 1.5 }}
              label="Reason for the expenditure"
              value={form.reason || ''}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="overline" fontWeight={800} color="text.secondary">
                What is needed
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip size="small" variant="outlined" sx={{ fontWeight: 800 }}
                label={`Σ GHS ${money(total)}`} />
            </Stack>

            <Stack spacing={1.2}>
              {items.map((it, i) => (
                <Box key={i} sx={{
                  p: 1.2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
                }}>
                  <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems="center">
                    <Chip size="small" label={i + 1} sx={{ fontWeight: 800 }} />
                    {/* getOptionLabel is spelled out on both rather than
                        left to the default. The default reaches into
                        `option.label`, which throws on anything that is
                        not a string or an object — and a render throw
                        here takes the entire page white. */}
                    <Autocomplete size="small" fullWidth options={categoryNames}
                      value={it.exp_cat || null}
                      getOptionLabel={(o) => String(o ?? '')}
                      isOptionEqualToValue={(o, v) => String(o) === String(v)}
                      onChange={(_, v) => patch(i, { exp_cat: v || '', exp_ty: '' })}
                      renderInput={(p) => <TextField {...p} label="Category *" />} />
                    <Autocomplete size="small" fullWidth options={typesFor(it.exp_cat)}
                      value={it.exp_ty || null} disabled={!it.exp_cat}
                      getOptionLabel={(o) => String(o ?? '')}
                      isOptionEqualToValue={(o, v) => String(o) === String(v)}
                      noOptionsText={it.exp_cat
                        ? `No types set up under "${it.exp_cat}"`
                        : 'Choose a category first'}
                      onChange={(_, v) => patch(i, { exp_ty: v || '' })}
                      renderInput={(p) => <TextField {...p} label="Type *" />} />
                    <TextField size="small" fullWidth label="Request holder *"
                      value={it.request_holder}
                      onChange={e => patch(i, { request_holder: e.target.value })} />
                    <IconButton size="small" onClick={() => dropItem(i)}
                      disabled={items.length === 1}><CloseIcon fontSize="small" /></IconButton>
                  </Stack>
                  <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} sx={{ mt: 1 }}>
                    <TextField size="small" fullWidth label="What it is for *"
                      value={it.narration}
                      onChange={e => patch(i, { narration: e.target.value })} />
                    <TextField size="small" label="Qty" type="number" sx={{ width: { lg: 90 } }}
                      value={it.quantity}
                      onChange={e => patch(i, { quantity: e.target.value })} />
                    <TextField size="small" label="Unit price" type="number" sx={{ width: { lg: 130 } }}
                      value={it.unit_price}
                      onChange={e => patch(i, { unit_price: e.target.value })} />
                    <TextField size="small" label="Or total" type="number" sx={{ width: { lg: 130 } }}
                      value={it.amount}
                      onChange={e => patch(i, { amount: e.target.value })} />
                    <Chip variant="outlined" sx={{ fontWeight: 800, minWidth: 120 }}
                      label={`GHS ${money(lineTotal(it))}`} />
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Button size="small" startIcon={<AddIcon />} onClick={addItem} sx={{ mt: 1.2, fontWeight: 700 }}>
              Add another item
            </Button>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* Say WHY it is disabled. A greyed-out button with no reason is
            the same dead end as a server error, just quieter. */}
        {problem && !saving && (
          <Typography sx={{ fontSize: 12, color: 'warning.dark', fontWeight: 700, flex: 1 }}>
            {problem}
          </Typography>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit}
          disabled={saving || !!problem}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <RequestIcon />}
          sx={{ fontWeight: 800 }}>
          {saving ? 'Submitting…' : 'Submit for approval'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Sign or refuse one request ────────────────────────────────────
function DecisionDialog({ open, request, decision, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const reject = decision === 'reject';

  useEffect(() => { if (open) { setNote(''); setError(''); } }, [open]);
  if (!request) return null;

  const go = async () => {
    setBusy(true); setError('');
    try {
      await API.post(`/expenditure-requests/${request.id}/${reject ? 'reject' : 'approve'}`, { note });
      swal.success(reject ? `${request.ref_no} refused` : `${request.ref_no} — ${request.awaiting} given`);
      onDone && (await onDone());
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not record the decision');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {reject ? 'Refuse this request' : request.awaiting}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {request.ref_no} · {request.requisitioner} · GHS {money(request.total_amount)}
        </Typography>
      </Box>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        <Box sx={{ mb: 2 }}>
          {(request.items || []).map((it, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.4 }}>
              • {it.narration} — <b>GHS {money(it.amount)}</b>{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                ({it.exp_cat} / {it.exp_ty})
              </Typography>
            </Typography>
          ))}
        </Box>
        {request.reason && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2, py: 0.2 }}>
            {request.reason}
          </Alert>
        )}
        <TextField fullWidth multiline minRows={2} size="small"
          label={reject ? 'Why are you refusing it? (required)' : 'Note (optional)'}
          value={note} onChange={e => setNote(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={reject ? 'error' : 'success'}
          onClick={go} disabled={busy || (reject && !note.trim())}
          startIcon={busy ? <CircularProgress size={16} color="inherit" />
            : reject ? <RejectIcon /> : <ApproveIcon />}
          sx={{ fontWeight: 800 }}>
          {busy ? 'Saving…' : reject ? 'Refuse' : 'Sign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── The ladder, drawn ─────────────────────────────────────────────
function Ladder({ request }) {
  const steps = request.ladder || [];
  if (!steps.length) return null;
  return (
    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
      {steps.map((s) => (
        <Tooltip key={s.id} title={
          s.done ? `${s.label} — ${s.by || 'signed'}${s.note ? ` · ${s.note}` : ''}`
                 : `${s.label} — not yet`}>
          <Chip size="small" variant={s.done ? 'filled' : 'outlined'}
            color={s.done ? 'success' : 'default'}
            icon={s.done ? <ApproveIcon sx={{ fontSize: 14 }} /> : <WaitingIcon sx={{ fontSize: 14 }} />}
            label={s.done ? (s.by || 'signed') : s.label}
            sx={{ height: 22, fontSize: 10.5, fontWeight: 700 }} />
        </Tooltip>
      ))}
    </Stack>
  );
}

// ── The panel ─────────────────────────────────────────────────────
export function ExpenditureRequestsPanel({ categories, expTypes, onPay, refreshKey = 0 }) {
  const [tab, setTab]       = useState(0);
  const [mine, setMine]     = useState([]);
  const [queue, setQueue]   = useState([]);
  const [payable, setPay]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [newOpen, setNew]   = useState(false);
  const [decision, setDec]  = useState({ open: false, request: null, decision: 'approve' });

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const [a, b, c] = await Promise.all([
        API.get('/expenditure-requests', { params: { mine: 1 } }).catch(() => ({ data: [] })),
        API.get('/expenditure-requests/queue').catch(() => ({ data: [] })),
        API.get('/expenditure-requests/payable').catch(() => ({ data: [] })),
      ]);
      setMine(a.data || []); setQueue(b.data || []); setPay(c.data || []);
    } finally { setLoad(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const rows = tab === 0 ? mine : tab === 1 ? queue : payable;

  const Row = ({ r }) => (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{r.ref_no}</TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={700}>{r.requisitioner}</Typography>
        <Typography variant="caption" color="text.secondary">
          {(r.items || []).map(i => i.narration).join(' · ').slice(0, 70)}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
        GHS {money(r.total_amount)}
      </TableCell>
      <TableCell>{fmtDate(r.requested_at)}</TableCell>
      <TableCell>
        <Chip size="small" color={STATUS_TONE[r.status] || 'default'}
          label={STATUS_LABEL[r.status] || r.status}
          sx={{ fontWeight: 700, height: 22, fontSize: 10.5 }} />
        {r.status === 'REJECTED' && r.reject_reason && (
          <Typography variant="caption" color="error.main" display="block">
            {r.rejected_at_level}: {r.reject_reason}
          </Typography>
        )}
      </TableCell>
      <TableCell><Ladder request={r} /></TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {tab === 1 && (
          <>
            <Button size="small" variant="contained" color="success" sx={{ fontWeight: 700, mr: 0.5 }}
              startIcon={<ApproveIcon />}
              onClick={() => setDec({ open: true, request: r, decision: 'approve' })}>
              Sign
            </Button>
            <Button size="small" variant="outlined" color="error" sx={{ fontWeight: 700 }}
              onClick={() => setDec({ open: true, request: r, decision: 'reject' })}>
              Refuse
            </Button>
          </>
        )}
        {tab === 2 && (
          <Button size="small" variant="contained" startIcon={<PayIcon />} sx={{ fontWeight: 800 }}
            onClick={() => onPay && onPay(r)}>
            Pay this
          </Button>
        )}
        {tab === 0 && r.status === 'PAID' && (
          <Chip size="small" variant="outlined" sx={{ fontWeight: 700 }}
            label={`${r.voucher_no || 'paid'}${r.cheque_no ? ` · ${r.cheque_no}` : ''}`} />
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Card sx={{ borderRadius: 2 }}>
      <Box sx={{
        px: 2, py: 1.4, borderBottom: 1, borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}>
        <RequestIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            Expenditure Requests
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ask before you spend — signed off, then paid
          </Typography>
        </Box>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}
          sx={{ textTransform: 'none', fontWeight: 700 }}>Reload</Button>
        <Button size="small" variant="contained" startIcon={<AddIcon />}
          onClick={() => setNew(true)} sx={{ fontWeight: 800 }}>
          New request
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ px: 1, borderBottom: 1, borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', minHeight: 44 } }}>
        <Tab label={`My requests (${mine.length})`} />
        <Tab label={`To approve (${queue.length})`} />
        <Tab label={`Ready to pay (${payable.length})`} />
      </Tabs>

      <CardContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {tab === 0 ? 'You have not raised any requests yet.'
               : tab === 1 ? 'Nothing is waiting for your signature.'
               : 'Nothing is approved and waiting for a cheque.'}
            </Typography>
            {tab === 1 && (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Requests you raised yourself never appear here — somebody else signs those.
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Ref</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Requisitioner / what</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="right">Amount</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Raised</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Signatures</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => <Row key={r.id} r={r} />)}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>

      <ExpenditureRequestDialog open={newOpen} onClose={() => setNew(false)}
        categories={categories} expTypes={expTypes} onSaved={load} />
      <DecisionDialog open={decision.open} request={decision.request}
        decision={decision.decision}
        onClose={() => setDec(d => ({ ...d, open: false }))} onDone={load} />
    </Card>
  );
}

export default ExpenditureRequestsPanel;
